import Image from 'next/image'
import Link from 'next/link'
import { RichText } from '@payloadcms/richtext-lexical/react'
import type { Post } from '@/payload-types'
import { mediaUrl } from '@/lib/seo/jsonld'
import { primaryCategory, postAuthor, resolvedTags } from '@/lib/relations'
import { formatDate, DATE_LONG } from '@/lib/format'
import { Breadcrumb } from './Breadcrumb'
import { PostMeta } from './PostMeta'
import { ShareButtons } from './ShareButtons'
import { articleConverters } from './RichTextConverters'
import SourcesBlock from './SourcesBlock'
import Provenance from './Provenance'

/**
 * Vista completa di un articolo (testata + corpo + tag + autore). ISOMORFA: nessun
 * import server-only, così lo stesso markup serve sia l'articolo SSR (page.tsx) sia
 * gli articoli caricati lato client dallo scroll infinito (<ArticleStream>).
 *
 * Il root <article> espone data-attribute (slug/href/title) letti dall'observer di
 * <ArticleStream> per cambiare URL + sparare il page_view GA4 quando l'articolo
 * entra al centro del viewport.
 */
export function ArticleView({
  post,
  siteName,
  siteUrl,
  priority = false,
}: {
  post: Post
  siteName: string
  /** Origin assoluto del tenant (es. https://explorae.it) per i link di condivisione. */
  siteUrl?: string
  priority?: boolean
}) {
  const author = postAuthor(post)
  const heroUrl = mediaUrl(post.featuredImage)
  const featured = post.featuredImage && typeof post.featuredImage === 'object' ? post.featuredImage : null
  const primary = primaryCategory(post)
  const date = formatDate(post.publishedAt, DATE_LONG)
  const tags = resolvedTags(post).filter((t) => t.slug)
  // Coerente col template metadata del layout: «<title> · <siteName>».
  const docTitle = `${post.seo?.title || post.title} · ${siteName}`
  const shareUrl = `${siteUrl ?? ''}/${post.slug}/`
  // Sintesi in testata (sotto il titolo, sopra la byline — era il box TL;DR nel corpo):
  // usa il `tldr` se presente, altrimenti ripiega sull'`excerpt` (sintesi 1-2 frasi),
  // così compare anche sui contenuti legacy privi di tldr.
  const summary = post.tldr?.trim() || post.excerpt?.trim() || ''

  return (
    <article data-article-slug={post.slug} data-article-href={`/${post.slug}/`} data-article-title={docTitle}>
      {heroUrl ? (
        // Testata immersiva: l'altezza NON è più un aspect-ratio puro. Con un 21:9
        // (es. 2560×1080) il 2.4/1 valeva ~1066px e, sommato allo header sticky (h-16),
        // spingeva titolo/byline sotto la piega — l'utente vedeva solo l'immagine.
        // Quindi: altezza = min(altezza da aspect-ratio, 72svh), così su schermi bassi
        // e larghi la testata resta dentro il viewport. `min-h` (non `h`) perché il
        // blocco di testo — ora con TL;DR — può essere più alto dell'immagine su mobile:
        // in quel caso la testata cresce invece di sbordare fuori dal box.
        <header className="relative isolate flex min-h-[62vw] items-end overflow-hidden sm:min-h-[min(50vw,72svh)] lg:min-h-[min(41.7vw,72svh)]">
          <Image
            src={heroUrl}
            alt={featured?.alt || post.title}
            fill
            priority={priority}
            // Elemento LCP della pagina articolo: senza fetchpriority esplicito
            // Chrome lo accoda come Low dietro i chunk JS (vedi FeaturedMosaic).
            fetchPriority={priority ? 'high' : undefined}
            sizes="100vw"
            className="-z-10 object-cover"
          />
          {/* Scrim: da mobile il blocco di testo (titolo + sintesi + byline) occupa gran
              parte della testata, quindi la parte scura sale fino al 75%; da sm in su la
              testata è più alta del testo e il gradiente torna leggero per non spegnere
              la foto. */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/85 via-black/55 via-75% to-transparent sm:via-black/45 sm:via-50%" />
          {/* Stessa colonna del corpo (`mx-auto max-w-3xl` dentro `container-wide`): il blocco
              di testata stava sul bordo pieno del container mentre l'articolo parte ~300px più a
              destra, quindi titolo e primo paragrafo cadevano su due assi verticali diversi. La
              didascalia della copertina, subito sotto, era già incolonnata col corpo: l'hero era
              l'unico pezzo fuori squadra. Le vecchie max-w interne (4xl sul titolo, 3xl su sintesi
              e provenienza) sparisco: la misura ora la detta la colonna, una sola per tutti. */}
          <div className="container-wide w-full pb-8 pt-32 sm:pb-12">
            <div className="mx-auto max-w-3xl">
              {primary ? (
                <Link
                  href={`/categoria/${primary.slug}/`}
                  className="inline-block rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm"
                >
                  {primary.name}
                </Link>
              ) : null}
              <h1 className="mt-3 text-3xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl">
                {post.title}
              </h1>
              {summary ? <p className="mt-4 leading-relaxed text-white/90 sm:text-lg">{summary}</p> : null}
              <PostMeta
                author={author}
                dateISO={post.publishedAt}
                dateLabel={date}
                className="mt-4 flex flex-wrap items-center gap-x-3 text-sm text-white/85"
              />
              {/* LA DICHIARAZIONE STAVA SOLO NEL RAMO SENZA COPERTINA, cioè non compariva su nessun
                  articolo generato — perché un articolo generato ha sempre una copertina. Misurato sulla
                  pagina pubblicata: zero occorrenze della striscia e zero della didascalia dell'immagine.
                  Su una pagina il cui testo è assistito da un modello e la cui immagine è INVENTATA da un
                  modello, la nota d'uso mancava esattamente nel caso per cui è stata scritta (art. 50
                  Reg. UE 2024/1689). */}
              <Provenance
                provenance={post.provenance}
                sourceCount={(post.sources ?? []).filter((s) => s?.url).length}
                className="mt-4 text-xs leading-relaxed text-white/75"
              />
            </div>
          </div>
        </header>
      ) : (
        <header className="container-wide pt-12 sm:pt-16">
          <div className="mx-auto max-w-3xl">
            {primary ? (
              <Link href={`/categoria/${primary.slug}/`} className="text-xs font-semibold uppercase tracking-wider text-accent-strong">
                {primary.name}
              </Link>
            ) : null}
            <h1 className="mt-2 text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl">{post.title}</h1>
            {summary ? <p className="mt-4 leading-relaxed text-zinc-600 sm:text-lg">{summary}</p> : null}
            <PostMeta
              author={author}
              dateISO={post.publishedAt}
              dateLabel={date}
              className="mt-4 flex flex-wrap items-center gap-x-3 text-sm text-zinc-500"
            />
            <Provenance provenance={post.provenance} sourceCount={(post.sources ?? []).filter((s) => s?.url).length} />
          </div>
        </header>
      )}

      {/* Didascalia della copertina: `media.caption` e `media.aiGenerated` erano scritti dal worker,
          persistiti, e non renderizzati da nessuna riga — quindi un'immagine generata da un modello
          arrivava in pagina senza dire di esserlo. Su un luogo reale e nominato una copertina inventata
          è un'affermazione visiva, ed è la più difficile da smentire per chi legge: la dichiarazione va
          accanto all'immagine, non solo nei metadati del database. */}
      {heroUrl && (featured?.caption || featured?.aiGenerated) ? (
        <div className="container-wide">
          <p className="mx-auto mt-3 max-w-3xl text-xs text-zinc-500">
            {featured?.caption || 'Immagine generata con AI'}
            {featured?.aiGenerated ? ' — non è una fotografia del luogo.' : ''}
          </p>
        </div>
      ) : null}

      <div className="container-wide">
        <div className="mx-auto max-w-3xl py-10 sm:py-14">
          <Breadcrumb
            className="mb-6"
            items={[
              { name: 'Home', href: '/' },
              ...(primary ? [{ name: primary.name, href: `/categoria/${primary.slug}/` }] : []),
              { name: post.title },
            ]}
          />

          <ShareButtons url={shareUrl} title={post.title} className="mb-8" />

          <div className="prose prose-zinc max-w-none text-justify hyphens-auto prose-headings:text-left prose-headings:tracking-tight prose-a:font-medium">
            <RichText data={post.content as never} converters={articleConverters} />
          </div>

          <SourcesBlock sources={post.sources} />

          {tags.length ? (
            <div className="mt-10 flex flex-wrap gap-2">
              {tags.map((t) => (
                <Link
                  key={t.id}
                  href={`/tag/${t.slug}/`}
                  className="rounded-full bg-zinc-100 px-3 py-1.5 text-sm font-medium text-zinc-600 transition-colors hover:bg-accent-soft hover:text-accent-strong"
                >
                  #{t.name}
                </Link>
              ))}
            </div>
          ) : null}

          {author ? (
            <div className="mt-14 flex items-center gap-4 border-t border-zinc-100 pt-8">
              <div className="grid size-12 shrink-0 place-items-center rounded-full bg-accent-soft text-lg font-bold text-accent-strong">
                {author.name?.charAt(0) ?? 'A'}
              </div>
              <div>
                {/* Nome + data di pubblicazione: la testata immersiva scrolla via, quindi la
                    firma in coda all'articolo è l'unico punto in cui il lettore ritrova
                    «chi» e «quando» senza risalire in cima. */}
                <p className="flex flex-wrap items-baseline gap-x-2 font-semibold text-ink">
                  {author.slug ? (
                    <Link href={`/autore/${author.slug}/`} className="transition-colors hover:text-accent-strong">
                      {author.name}
                    </Link>
                  ) : (
                    author.name
                  )}
                  {date ? (
                    <span className="text-sm font-normal text-zinc-500">
                      <span aria-hidden className="opacity-40">
                        ·
                      </span>{' '}
                      Pubblicato il <time dateTime={post.publishedAt ?? undefined}>{date}</time>
                    </span>
                  ) : null}
                </p>
                {author.bio ? <p className="text-sm text-zinc-500">{author.bio}</p> : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </article>
  )
}
